import { apiFetch } from '@/lib/api/client';
import type { PagedData } from '@/lib/api/types';
import type {
  CreateStudentInput,
  StudentView,
  UpdatePreferredMethodInput,
  UpdateStudentInput,
} from '@/features/students/types';

/** `GET /students`. Tenant-scoped (ADR-026 Addendum, AO-2). Narrowed to Self/Child server-side for a non-broad-access caller. */
export async function listStudents(page = 1, limit = 20): Promise<PagedData<StudentView>> {
  return apiFetch<PagedData<StudentView>>(`/students?page=${String(page)}&limit=${String(limit)}`, {
    tenantScoped: true,
  });
}

/** `GET /students/:id`. Tenant-scoped (AO-2). */
export async function getStudent(id: string): Promise<StudentView> {
  return apiFetch<StudentView>(`/students/${id}`, { tenantScoped: true });
}

/** `POST /students`. Tenant-scoped; requires `students:manage`. */
export async function createStudent(input: CreateStudentInput): Promise<StudentView> {
  return apiFetch<StudentView>('/students', { method: 'POST', body: input, tenantScoped: true });
}

/** `PATCH /students/:id`. Tenant-scoped; requires `students:manage`. */
export async function updateStudent(id: string, input: UpdateStudentInput): Promise<StudentView> {
  return apiFetch<StudentView>(`/students/${id}`, {
    method: 'PATCH',
    body: input,
    tenantScoped: true,
  });
}

/**
 * `PATCH /students/:id/preferred-method` (Phase 8, Pilar A). Tenant-scoped;
 * gated on `students:read` at the route, narrowed by the server to the
 * student themselves, an assigned Teacher, or an Organization Admin.
 */
export async function updatePreferredMethod(
  id: string,
  input: UpdatePreferredMethodInput,
): Promise<StudentView> {
  return apiFetch<StudentView>(`/students/${id}/preferred-method`, {
    method: 'PATCH',
    body: input,
    tenantScoped: true,
  });
}

/**
 * `DELETE /students/:id`. Tenant-scoped; requires `students:manage`. Soft
 * deactivation (`deactivateStudent`'s doc comment) - there is no reactivation
 * endpoint, so this is not reversible from within the application.
 */
export async function deactivateStudent(id: string): Promise<void> {
  await apiFetch<unknown>(`/students/${id}`, { method: 'DELETE', tenantScoped: true });
}
